import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import Button from 'carbon-react/lib/components/button';
import { Select, Option } from 'carbon-react/lib/components/select';
import Box from 'carbon-react/lib/components/box';

import { UILoader, toastError } from '@futrli/ui-components';
import { FinancialConfig } from '@futrli-sdk/predict';
import { Logger, QUERY_PARAMS, ROUTES, getForecastByIdOnboardingUrl, getMonthName } from '@futrli/shared-services';

import * as styles from './onboarding.scss';
import { fetchFinancialConfig } from '../../../../predict/src/core/api/financial-config';
import { getMonthsBetweenDates } from '../../../../predict/src/utils/dates';
import {
  BUDGET_ONBOARDING_STEP,
  LS_FORECASTING_ONBOARDING_STEP
} from '../../../../predict/src/forecast/onboarding/const';
import { OnboardingPageTitle } from '../../../../predict/src/forecast/onboarding/components/onboarding-page-title';
import { ForecastingNavigationTabs } from '../../../../predict/src/forecast/detail/predictions/navigation/forecasting-navigation-tabs';
import { CreateNewForecastPayload, ForecastType } from '../../../../predict/src/forecast/dashboard/types';
import { createNewForecast } from '../../../../predict/src/core/api/forecast';
import { trackCreateForecastEvent } from '../../../../predict/src/forecast/dashboard/utils';
import { useSelectAndLoadWorkspaces } from '../hooks/use-select-and-load-workspaces';
import { EndOnboardingFlow } from '../../../../predict/src/forecast/onboarding/components/end-onboarding-flow';

export const BudgetDateSelect = () => {
  const intl = useIntl();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const workspaceId = searchParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = searchParams.get(QUERY_PARAMS.ORG_ID);

  const [isLoading, setIsLoading] = useState(true);
  const [finConfig, setFinConfig] = useState<FinancialConfig>();
  const [selectedStartDate, setSelectedStartDate] = useState('');

  const navigate = useNavigate();

  const { isLoading: isLoadingAppData } = useSelectAndLoadWorkspaces();

  useEffect(() => {
    const fetchData = async () => {
      if (!workspaceId || !organisationId) {
        return;
      }
      try {
        const fetchedFinancialConfig = await fetchFinancialConfig(workspaceId, organisationId);
        setFinConfig(fetchedFinancialConfig);
        setSelectedStartDate(fetchedFinancialConfig.current_year.start);
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [organisationId, workspaceId]);

  const options = useMemo(() => {
    if (finConfig) {
      const currentYearEndInfo = finConfig.current_year.end.split('-');
      // eslint-disable-next-line no-magic-numbers
      const nextYearEnd = `${Number(currentYearEndInfo[0]) + 1}-${currentYearEndInfo[1]}-${currentYearEndInfo[2]}`;
      return getMonthsBetweenDates(finConfig.current_year.start, nextYearEnd).map(strDates => ({
        id: strDates,
        name: `${getMonthName(Number(strDates.split('-')[1]))} ${strDates.split('-')[0]}`
      }));
    }
    return [];
  }, [finConfig]);

  const handleMonthClick = (date: string) => {
    setSelectedStartDate(date);
  };

  const handleNextClick = async () => {
    if (!workspaceId || !organisationId) {
      return;
    }

    try {
      setIsLoading(true);
      const requestBody: CreateNewForecastPayload = {
        name: intl.formatMessage({ id: 'predict.my_first_forecast', defaultMessage: 'My first forecast' }),
        kind: 'budget-from-scratch',
        categories_with_ai_enabled: null,
        forecast_from_date: format(new Date(selectedStartDate), 'yyyy-MM-dd'),
        include_unpaid_invoices: false
      };

      const createdForecast = await createNewForecast(workspaceId, organisationId, requestBody);
      trackCreateForecastEvent(ForecastType.BUDGET);
      localStorage.setItem(LS_FORECASTING_ONBOARDING_STEP, BUDGET_ONBOARDING_STEP.SELECT_TYPE);
      navigate(
        `${getForecastByIdOnboardingUrl(workspaceId, organisationId, createdForecast.id, true)}/${
          BUDGET_ONBOARDING_STEP.SELECT_TYPE
        }${ROUTES.SALES}?${QUERY_PARAMS.ONBOARDING_FORECAST_ID}=${createdForecast.id}`
      );
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStartDate(event.target.value);
  };

  if (isLoading || isLoadingAppData) {
    return <UILoader isFull />;
  }

  if (!workspaceId || !organisationId) {
    return null;
  }
  return (
    <>
      <div className={styles.contentContainer}>
        <OnboardingPageTitle
          title={<FormattedMessage id="onboarding.start_month" defaultMessage="What month do you want it to start?" />}
          description={
            <FormattedMessage
              id="onboarding.first_month_of_fy"
              defaultMessage="Usually this is the first month of your financial year."
            />
          }
        />
        <div style={{ display: 'flex' }}>
          <Box width="300px">
            <Select
              autoComplete="off"
              onChange={handleDateChange}
              value={selectedStartDate}
              size="large"
              listMaxHeight={1000}
              aria-label={intl.formatMessage({
                id: 'onboarding.select_forecast_start_month.aria-label',
                defaultMessage: 'Select forecast start month'
              })}
            >
              {options.map(o => (
                <Option key={o.id} value={o.id} onClick={() => handleMonthClick(o.id)} text={o.name} />
              ))}
            </Select>
          </Box>
          <Button disabled={!selectedStartDate} ml="30px" onClick={handleNextClick} buttonType="primary" size="large">
            <FormattedMessage id="common.next" defaultMessage="Next" />
          </Button>
        </div>
        <div style={{ marginTop: 30 }}>
          <ForecastingNavigationTabs disabled />
        </div>
      </div>
      <EndOnboardingFlow />
    </>
  );
};
